import React, { FC, useEffect } from "react";

import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
  Table,
} from "reactstrap";

import ReactEcharts from "echarts-for-react";

import Breadcrumbs from "../../shared/base-components/Breadcrumbs";
import { useStatisticListMutation } from "./Statistics.actions";
import {
  CategoryWithTotalCost,
  MaintenanceTypes,
  Personals,
  TotalMaintenanceMaterial,
  formatDate,
  getDateDaysAgo,
} from "./Statistics.constants";
import { SubmitHandler, useForm } from "react-hook-form";
import Button from "../../shared/base-components/Button";
import DatePicker from "../../shared/base-components/DatePicker";
import SelectBox from "../../shared/base-components/SelectBox";
import { formatDecimal } from "../../shared/utils/formatText";

const Statistics: FC = () => {
  document.title =
    "İstatistik  | Bu ekranda şirketiniz için istatistik verilerini görüntülyebilirsiniz.";

  const isMobile: boolean = /iPhone|iPad|iPod|Android/i.test(
    navigator.userAgent
  );

  const { data, mutate, isLoading } = useStatisticListMutation();

  const { control, handleSubmit, setValue } = useForm();

  const onSubmit: SubmitHandler<any> = (requestValues: any) => {
    mutate({
      startDay: formatDate(requestValues.startDay),
      endDay: formatDate(requestValues.endDay),
      isForm: true,
    });
  };

  useEffect(() => {
    mutate({});
  }, []);

  const getOptionMaintenanceMaterial = (
    total_maintenance_material: TotalMaintenanceMaterial[]
  ) => {
    let devicePartNames = total_maintenance_material.map(
      (item) => item.device_part_name
    );

    let reformattedData = total_maintenance_material.map((item) => ({
      name: item.device_part_name,
      value: item.total,
    }));

    return {
      toolbox: {
        show: !1,
      },
      tooltip: {
        trigger: "item",
        formatter: "{a} <br/>{b}: {c} ({d}%)",
      },
      legend: {
        orient: "vertical",
        x: "left",
        data: devicePartNames,
        formatter: (name: string) => {
          const item = total_maintenance_material.find(
            (item) => item.device_part_name === name
          );
          return `${name} (${item?.total}) `;
        },
        textStyle: {
          color: ["#adb5bd"],
        },
      },
      color: [
        "#5156be",
        "#ffbf53",
        "#fd625e",
        "#4ba6ef",
        "#2ab57d",
        "#5156be",
        "#ffbf53",
        "#fd625e",
        "#4ba6ef",
        "#2ab57d",
        "#5156be",
        "#ffbf53",
        "#fd625e",
        "#4ba6ef",
        "#2ab57d",
        "#5156be",
        "#ffbf53",
        "#fd625e",
        "#4ba6ef",
        "#2ab57d",
      ],
      series: [
        {
          name: "Toplam Bakım",
          type: "pie",
          radius: ["50%", "70%"],
          avoidLabelOverlap: !1,
          label: {
            normal: {
              show: !1,
              position: "center",
            },
            emphasis: {
              show: !0,
              textStyle: {
                fontSize: "30",
                fontWeight: "bold",
              },
            },
          },
          labelLine: {
            normal: {
              show: !1,
            },
          },
          data: reformattedData,
        },
      ],
    };
  };

  const getOptionTotalCost = (
    categories_with_total_cost: CategoryWithTotalCost[]
  ) => {
    let devicePartNames = categories_with_total_cost.map((item) => item.name);

    let reformattedData = categories_with_total_cost.map((item) => ({
      name: item.name,
      value: item.total_cost,
    }));

    return {
      toolbox: {
        show: false,
      },
      tooltip: {
        trigger: "item",
        formatter: "{a} <br/>{b} : {c} ({d}%)",
      },
      legend: {
        orient: "vertical",
        left: "left",
        data: devicePartNames,
        formatter: (name: string) => {
          const item = categories_with_total_cost.find(
            (item) => item.name === name
          );
          return `${name} (${
            item?.total_cost === null ? 0 : item?.total_cost
          } ₺) `;
        },
        textStyle: {
          color: ["#858d98"],
        },
      },
      color: [
        "#fd625e",
        "#2ab57d",
        "#4ba6ef",
        "#ffbf53",
        "#5156be",
        "#fd625e",
        "#2ab57d",
        "#4ba6ef",
        "#ffbf53",
        "#5156be",
        "#fd625e",
        "#2ab57d",
        "#4ba6ef",
        "#ffbf53",
        "#5156be",
      ],
      series: [
        {
          name: "Toplam",
          type: "pie",
          radius: "55%",
          center: ["50%", "60%"],
          data: reformattedData,
          itemStyle: {
            emphasis: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        },
      ],
    };
  };

  const getOptionPersonalTotal = (categories_with_total_cost: Personals[]) => {
    let devicePartNames = categories_with_total_cost.map(
      (item) => item.fullname
    );

    let reformattedData = categories_with_total_cost.map((item) => ({
      name: item.fullname,
      value: item.total,
    }));

    return {
      toolbox: {
        show: false,
      },
      tooltip: {
        trigger: "item",
        formatter: "{a} <br/>{b} : {c} ({d}%)",
      },
      legend: {
        orient: "vertical",
        left: "left",
        data: devicePartNames,
        formatter: (name: string) => {
          const item = categories_with_total_cost.find(
            (item) => item.fullname === name
          );
          return `${name} (${item?.total === null ? 0 : item?.total}) `;
        },
        textStyle: {
          color: ["#858d98"],
        },
      },
      color: [
        "#fd625e",
        "#2ab57d",
        "#4ba6ef",
        "#ffbf53",
        "#5156be",
        "#fd625e",
        "#2ab57d",
        "#4ba6ef",
        "#ffbf53",
        "#5156be",
        "#fd625e",
        "#2ab57d",
        "#4ba6ef",
        "#ffbf53",
        "#5156be",
      ],
      series: [
        {
          name: "Toplam",
          type: "pie",
          radius: "55%",
          center: ["50%", "60%"],
          data: reformattedData,
          itemStyle: {
            emphasis: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        },
      ],
    };
  };

  const getMaintenanceTypes = (maintenance_types: MaintenanceTypes[]) => {
    let devicePartNames = maintenance_types.map((item) =>
      item?.maintenance_type === "maintenance" ? "Bakım" : "Montaj"
    );

    let reformattedData = maintenance_types.map((item) => ({
      name: item?.maintenance_type === "maintenance" ? "Bakım" : "Montaj",
      value: item?.total,
    }));

    return {
      toolbox: {
        show: false,
      },
      tooltip: {
        trigger: "item",
        formatter: "{a} <br/>{b} : {c} ({d}%)",
      },
      legend: {
        orient: "vertical",
        left: "left",
        data: devicePartNames,
        formatter: (name: string) => {
          let type = "";
          if (name === "Bakım") {
            type = "maintenance";
          } else {
            type = "setup";
          }
          const item = maintenance_types.find(
            (item) => item.maintenance_type === type
          );
          return `${name} (${item?.total === null ? 0 : item?.total}) `;
        },

        textStyle: {
          color: ["#858d98"],
        },
      },
      color: [
        "#fd625e",
        "#2ab57d",
        "#4ba6ef",
        "#ffbf53",
        "#5156be",
        "#fd625e",
        "#2ab57d",
        "#4ba6ef",
        "#ffbf53",
        "#5156be",
        "#fd625e",
        "#2ab57d",
        "#4ba6ef",
        "#ffbf53",
        "#5156be",
      ],
      series: [
        {
          name: "Toplam",
          type: "pie",
          radius: "55%",
          center: ["50%", "60%"],
          data: reformattedData,
          itemStyle: {
            emphasis: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        },
      ],
    };
  };

  return (
    <React.Fragment>
      <div className={isMobile ? "page-content-mobile" : "page-content"}>
        <Container fluid>
          <Breadcrumbs title="İstatistik Listesi" breadcrumbItem="İstatistik" />

          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  <Row className="align-items-end justify-content-end">
                    <Col sm={12} md={12} lg={12}>
                      <Form
                        onSubmit={handleSubmit(onSubmit)}
                        className="custom-form"
                      >
                        <Row className="align-items-center justify-content-end">
                          <Col sm="12" md="3" lg="2">
                            <FormGroup>
                              <Button
                                onClick={() => {
                                  setValue("startDay", getDateDaysAgo(7));
                                  setValue("endDay", new Date());
                                }}
                                text="Son 7 Gün"
                                variant="primary"
                              />
                            </FormGroup>
                          </Col>

                          <Col sm="12" md="3" lg="2">
                            <FormGroup>
                              <Button
                                onClick={() => {
                                  setValue("startDay", getDateDaysAgo(30));
                                  setValue("endDay", new Date());
                                }}
                                text="Son 30 Gün"
                                variant="primary"
                              />
                            </FormGroup>
                          </Col>

                          <Col sm="12" md="3" lg="2">
                            <FormGroup>
                              <SelectBox
                                control={control}
                                name="appointment_type"
                                defaultValue={60}
                                className="form-control"
                                onChangeHandler={(e) => {
                                  setValue(
                                    "startDay",
                                    getDateDaysAgo(parseInt(e.target.value))
                                  );
                                  setValue("endDay", new Date());
                                  handleSubmit(onSubmit)();
                                }}
                                options={[
                                  { key: "Son 60 Gün", value: 60 },
                                  { key: "Son 120 Gün", value: 120 },
                                  { key: "Son 180 Gün", value: 180 },
                                  { key: "Son 360 Gün", value: 360 },
                                ]}
                              />
                            </FormGroup>
                          </Col>

                          <Col sm="12" md="4" lg="2">
                            <FormGroup>
                              <Label className="form-label">Başlangıç</Label>
                              <DatePicker
                                control={control}
                                name="startDay"
                                className="form-control"
                                onChangeHandler={() => handleSubmit(onSubmit)()}
                                defaultValue={new Date()}
                              />
                            </FormGroup>
                          </Col>

                          <Col sm="12" md="4" lg="2">
                            <FormGroup>
                              <Label htmlFor="validationCustom02">Bitiş</Label>
                              <DatePicker
                                control={control}
                                name="endDay"
                                className="form-control"
                                onChangeHandler={() => handleSubmit(onSubmit)()}
                                defaultValue={new Date()}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </Form>
                    </Col>
                  </Row>
                  <div className="p-4 border rounded">
                    <div className="table-responsive">
                      <Table className="table-nowrap align-middle mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: "70px" }}>Toplam Müşteri</th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th className="text-end" style={{ width: "120px" }}>
                              <h5 className="font-size-15 mb-1">
                                {data?.total_customer}
                              </h5>
                            </th>
                          </tr>

                          <tr>
                            <th style={{ width: "70px" }}>
                              Toplam Oluşturulan Ödemeler
                            </th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th className="text-end" style={{ width: "120px" }}>
                              <h5 className="font-size-15 mb-1">
                                {data?.customer_total_payment} ₺
                              </h5>
                            </th>
                          </tr>

                          <tr>
                            <th style={{ width: "70px" }}>
                              Toplam Alınacak Ödemeler
                            </th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th className="text-end" style={{ width: "120px" }}>
                              <h5 className="font-size-15 mb-1">
                                {data?.total_due} ₺
                              </h5>
                            </th>
                          </tr>

                          <tr>
                            <th style={{ width: "70px" }}>Toplam Kar</th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th className="text-end" style={{ width: "120px" }}>
                              <h5 className="font-size-15 mb-1">
                                {formatDecimal(
                                  (data?.total_earned || 0) -
                                    (data?.total_cost || 0)
                                  // -
                                  //   (data?.total_device_buy || 0) -
                                  //   (data?.total_device_part_buy || 0) +
                                  //   (data?.total_device_part_sell || 0) +
                                  //   (data?.total_device_sell || 0)
                                )}{" "}
                                ₺
                              </h5>
                            </th>
                          </tr>
                        </thead>
                      </Table>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {data?.maintenance_types.length && (
            <Row>
              <Col xl={12}>
                <Card>
                  <CardHeader>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h5 className="card-title mb-0">
                          Montaj ve bakım sayıları
                        </h5>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <React.Fragment>
                      <ReactEcharts
                        style={{ height: "350px" }}
                        option={getMaintenanceTypes(data?.maintenance_types)}
                        className="apex-charts"
                      />
                    </React.Fragment>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {data?.total_maintenance_material.length && (
            <Row>
              <Col xl={12}>
                <Card>
                  <CardHeader>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h5 className="card-title mb-0">
                          Toplam Bakım'da Kullanılan Malzemeler
                        </h5>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <React.Fragment>
                      <ReactEcharts
                        style={{ height: "350px" }}
                        option={getOptionMaintenanceMaterial(
                          data?.total_maintenance_material
                        )}
                        className="apex-charts"
                      />
                    </React.Fragment>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {data?.total_maintenance_material_repair.length && (
            <Row>
              <Col xl={12}>
                <Card>
                  <CardHeader>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h5 className="card-title mb-0">
                          Toplam Bakımda Onarılan Malzemeler
                        </h5>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <React.Fragment>
                      <ReactEcharts
                        style={{ height: "350px" }}
                        option={getOptionMaintenanceMaterial(
                          data?.total_maintenance_material_repair
                        )}
                        className="apex-charts"
                      />
                    </React.Fragment>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {data?.categories_with_total_cost.length && (
            <Row>
              <Col xl={12}>
                <Card>
                  <CardHeader>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h5 className="card-title mb-0">Toplam Giderler</h5>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <React.Fragment>
                      <ReactEcharts
                        style={{ height: "350px" }}
                        option={getOptionTotalCost(
                          data?.categories_with_total_cost
                        )}
                        className="apex-charts"
                      />
                    </React.Fragment>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {data?.personals.length && (
            <Row>
              <Col xl={12}>
                <Card>
                  <CardHeader>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h5 className="card-title mb-0">
                          Toplam Personel Bakımları
                        </h5>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <React.Fragment>
                      <ReactEcharts
                        style={{ height: "350px" }}
                        option={getOptionPersonalTotal(data?.personals)}
                        className="apex-charts"
                      />
                    </React.Fragment>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Statistics;
